import { useState } from 'react';
import { type Control, type UseFormSetValue, useWatch } from 'react-hook-form';
import {
  formatDisplayLabel,
  parseDisplayLabel,
} from '../../shared/formatDisplayLabel';
import type { AutoAssignmentRule } from '../rulesApi';
import { areConditionsEqual } from './ruleConditionsUtils';
import { filterToConditions } from './ruleFilterMapping';
import type { RuleFormValues } from './ruleFormSchema';

export type RuleFormView = 'main' | 'labels' | 'test';

type UseRuleFormViewsParams = {
  control: Control<RuleFormValues>;
  setValue: UseFormSetValue<RuleFormValues>;
  formType: 'add' | 'edit';
  rule?: AutoAssignmentRule;
};

const PANEL_TITLE_BY_VIEW: Record<Exclude<RuleFormView, 'main'>, string> = {
  labels: 'Label selection',
  test: 'Rule test',
};

export const useRuleFormViews = ({
  control,
  setValue,
  formType,
  rule,
}: UseRuleFormViewsParams) => {
  const [view, setView] = useState<RuleFormView>('main');
  const [pickerDraftLabels, setPickerDraftLabels] = useState<string[]>([]);
  const [testSnapshot, setTestSnapshot] = useState<{
    conditions: RuleFormValues['conditions'];
  } | null>(null);
  const [initialTestConditions, setInitialTestConditions] = useState<
    RuleFormValues['conditions'] | null
  >(null);

  const labels = useWatch({ control, name: 'labels' }) ?? [];
  const conditions = useWatch({ control, name: 'conditions' }) ?? [];
  const canTest = conditions.length > 0 && conditions.every((c) => c.regex);
  const hasTestModifications =
    initialTestConditions !== null &&
    !areConditionsEqual(conditions, initialTestConditions);

  const panelTitle =
    view === 'main'
      ? formType === 'add'
        ? 'New rule'
        : 'Edit rule'
      : PANEL_TITLE_BY_VIEW[view];

  const goToMainView = () => setView('main');

  const openLabelPicker = () => {
    setPickerDraftLabels(
      labels.map((l) => formatDisplayLabel(l.name, l.value)),
    );
    setView('labels');
  };

  const applyPickerDraft = () => {
    const structured = pickerDraftLabels.map((dl) => {
      const parsed = parseDisplayLabel(dl);
      return { name: parsed.name, value: parsed.value ?? '' };
    });
    setValue('labels', structured, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setView('main');
  };

  const openTestView = () => {
    const snapshot = [...conditions];
    setInitialTestConditions(snapshot);
    setTestSnapshot({ conditions: snapshot });
    setView('test');
  };

  const applyTestChanges = () => {
    setView('main');
    setTestSnapshot(null);
    setInitialTestConditions(null);
  };

  const revertTestChanges = () => {
    if (initialTestConditions) {
      const originalConditions: RuleFormValues['conditions'] =
        formType === 'edit' && rule ? filterToConditions(rule.filter) : [];
      setValue('conditions', initialTestConditions, {
        shouldDirty: !areConditionsEqual(
          initialTestConditions,
          originalConditions,
        ),
        shouldValidate: true,
      });
    }
    setView('main');
    setTestSnapshot(null);
    setInitialTestConditions(null);
  };

  const runTest = () => {
    setTestSnapshot({ conditions: [...conditions] });
  };

  const clearTestSnapshot = () => {
    setTestSnapshot(null);
  };

  return {
    view,
    panelTitle,
    conditions,
    canTest,
    hasTestModifications,
    pickerDraftLabels,
    setPickerDraftLabels,
    testSnapshot,
    goToMainView,
    openLabelPicker,
    applyPickerDraft,
    openTestView,
    applyTestChanges,
    revertTestChanges,
    runTest,
    clearTestSnapshot,
  };
};
